import { noop } from 'underscore';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Stack } from '@fluentui/react';
import {
  getUrlPrefixAndDestination,
  getUpdatedFullUrlOnPrefixUpdate,
  getUpdatedFullUrlOnTextUpdate,
} from '@bingads-webui-campaign-react/url-input-fluent';

import { BaseTextField } from './base-text-field';
import { Error } from './error';
import { withError } from './with-error';
import { contentStyles } from '../styles';

const urlPrefixOptions = [
  { key: 'https', text: 'https://', title: 'https://' },
  { key: 'http', text: 'http://', title: 'http://' },
];

export function UrlInput(props) {
  const {
    config,
    url,
    i18n,
    onChange,
    onBlur,
  } = props;
  const UrlTextField = useMemo(() =>
    withError(BaseTextField, config.key), [config.key]);

  const [urlPrefix, urlWithoutPrefix] = useMemo(
    () => getUrlPrefixAndDestination(url),
    [url]
  );

  const [key, setKey] = useState(0);

  const onUrlPrefixChanged = useCallback(
    (event, newItem) =>
      onChange([getUpdatedFullUrlOnPrefixUpdate(urlWithoutPrefix, newItem.key)], config.key),
    [config.key, onChange, urlWithoutPrefix]
  );

  const onTextChange = useCallback((value) => {
    const newValue = getUpdatedFullUrlOnTextUpdate(urlPrefix, value);

    if (newValue) {
      if (newValue === value) { // this means value contains protocol
        // forceUpdate UrlTextField, need to ensure the prefix removed. https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/4027143/
        setKey(key + 1);
      }
      onChange([getUpdatedFullUrlOnTextUpdate(urlPrefix, value)], config.key);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.key, onChange, urlPrefix]);

  return (
    <Stack
      className={config.className}
      tokens={contentStyles.errorStackToken}
    >
      <Stack
        horizontal
        tokens={contentStyles.urlFieldStackToken}
      >
        <Dropdown
          options={urlPrefixOptions}
          selectedKey={urlPrefix}
          onChange={onUrlPrefixChanged}
          ariaLabel={i18n.getString(config.dropdownLabel)}
          className="url-protocol"
        />
        <Stack.Item grow>
          <UrlTextField
            key={key}
            configProps={config}
            i18n={i18n}
            value={urlWithoutPrefix}
            updateItem={onTextChange}
            onBlur={onBlur}
            optional={config.optional}
            inputClassName="url-field"
          />
        </Stack.Item>
      </Stack>
      <Error property={config.key} />
    </Stack>
  );
}

UrlInput.propTypes = {
  config: PropTypes.shape({
    className: PropTypes.string,
    key: PropTypes.string.isRequired,
  }).isRequired,

  url: PropTypes.string.isRequired,

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,

  onChange: PropTypes.func,

  onBlur: PropTypes.func,
};

UrlInput.defaultProps = {
  onChange: noop,
  onBlur: noop,
};
